import { Badge, Button, Card, Flex, Space, Tabs } from 'antd';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  hasAccountsAccess,
  hasDesignAccess,
  hasSalesAccess,
  isDesignTaskVisibleToUser,
  isTaskVisibleOnPersonalDashboard,
  tasksForQueue,
} from '../access';
import { TaskCard, WorkerTaskModal, type TaskVerb } from '../components';
import { useZenBootstrap } from '../hooks';
import { LOGISTICS_DEPARTMENT_ID } from '../status';
import type { Department, DepartmentTask } from '../types';
import { CardGridSkeleton, EmptyState } from '../ui';
import { useTaskActions } from '../useTaskActions';

function pendingEmptyCopy(department: Department | undefined): { title: string; description: string } {
  const name = department?.name ?? 'this department';
  if (department?.id === 'dept-design') {
    return { title: `No jobs waiting for ${name}`, description: 'New jobs appear here when Accounts records an invoice.' };
  }
  if (department?.id === LOGISTICS_DEPARTMENT_ID) {
    return { title: `No jobs waiting for ${name}`, description: 'Jobs appear here once every selected department has finished production.' };
  }
  return { title: `No jobs waiting for ${name}`, description: 'Jobs appear here as soon as Design finishes the artwork.' };
}

export function QueuePage({ completed = false, departmentId }: { completed?: boolean; departmentId?: string }) {
  const { data, isLoading } = useZenBootstrap();
  const navigate = useNavigate();
  const runTaskAction = useTaskActions(data?.currentUser);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const assignedQueueDepartments = data?.departments.filter((department) =>
    department.active && department.queueEnabled && data.currentUser.departmentIds.includes(department.id)) ?? [];
  const defaultDepartmentId = departmentId && departmentId !== 'all'
    ? departmentId
    : assignedQueueDepartments[0]?.id;
  const [departmentFilter, setDepartmentFilter] = useState(defaultDepartmentId ?? '');
  if (isLoading || !data) return <CardGridSkeleton />;
  const user = data.currentUser;
  const activeDepartmentFilter = assignedQueueDepartments.some((department) => department.id === departmentFilter)
    ? departmentFilter
    : assignedQueueDepartments[0]?.id ?? '';
  const activeDepartment = assignedQueueDepartments.find((department) => department.id === activeDepartmentFilter);
  const byId = new Map(data.workOrders.map((order) => [order.id, order]));
  const visibleForUser = data.tasks
    .filter((task) => user.departmentIds.includes(task.departmentId))
    .filter((task) => isTaskVisibleOnPersonalDashboard(task, user.id))
    .filter((task) => isDesignTaskVisibleToUser(task, byId.get(task.workOrderId)?.contactPerson, user))
    .filter((task) => byId.get(task.workOrderId)?.status !== 'cancelled');
  // Tab badges always reflect open work — never completed history.
  const openQueueTasks = tasksForQueue(visibleForUser, false).filter((task) => Boolean(task.availableAt));
  const queueTasks = completed ? tasksForQueue(visibleForUser, true) : openQueueTasks;
  const countForDepartment = (id: string) => openQueueTasks.filter((task) => task.departmentId === id && task.status === 'pending').length;
  const showTabCounts = !completed && assignedQueueDepartments.length > 1;
  const departmentTabLabel = (name: string, id: string): ReactNode => {
    if (!showTabCounts) return name;
    const count = countForDepartment(id);
    return (
      <span className="zen-queue-tab-label">
        {name}
        {count > 0 ? <Badge count={count} color="var(--ua-action)" style={{ marginLeft: 8 }} /> : null}
      </span>
    );
  };
  const tasks = queueTasks.filter((task) => !activeDepartmentFilter || task.departmentId === activeDepartmentFilter);
  const selectedTask = tasks.find((task) => task.id === selectedTaskId) ?? null;
  const selectedOrder = selectedTask ? byId.get(selectedTask.workOrderId) ?? null : null;
  const canUpload = hasDesignAccess(user);
  const filterTabs = [
    ...(hasSalesAccess(user) ? [{ key: 'sales', label: 'Sales' }] : []),
    ...(hasAccountsAccess(user) ? [{ key: 'accounts', label: 'Accounts' }] : []),
    ...assignedQueueDepartments.map((department) => ({
      key: department.id,
      label: departmentTabLabel(department.name, department.id),
    })),
  ];
  const changeWorkspace = (key: string) => {
    if (key === 'sales') navigate('/work-orders');
    else if (key === 'accounts') navigate('/accounts');
    else setDepartmentFilter(key);
  };

  const act = (task: DepartmentTask, action: TaskVerb) => {
    const order = byId.get(task.workOrderId);
    if (!order) return;
    runTaskAction(task, action, order);
    if (action === 'finish') setSelectedTaskId(null);
  };

  const card = (task: DepartmentTask, mine = true) => {
    const order = byId.get(task.workOrderId);
    return order && (
      <TaskCard
        key={task.id}
        task={task}
        order={order}
        onOpen={() => setSelectedTaskId(task.id)}
        onAction={!completed && mine ? act : undefined}
        readOnly={completed || !mine}
        showUpload={canUpload}
      />
    );
  };

  const modal = (
    <WorkerTaskModal
      task={selectedTask}
      order={selectedOrder}
      departments={data.departments}
      canUpload={canUpload}
      onAction={!completed && selectedTask && (
        selectedTask.status === 'pending'
        || (selectedTask.status === 'started' && selectedTask.pickedUpBy === user.id)
      ) ? act : undefined}
      onClose={() => setSelectedTaskId(null)}
    />
  );

  if (completed) {
    return (
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <Flex justify="flex-end"><Link to="/queue"><Button>Back to my queue</Button></Link></Flex>
        <Tabs activeKey={activeDepartmentFilter} onChange={changeWorkspace} items={filterTabs} />
        <QueueColumn title="Completed" count={tasks.length} empty={<EmptyState title="Nothing finished yet" description="Jobs you finish appear here." />}>
          {tasks.map((task) => card(task, false))}
        </QueueColumn>
        {modal}
      </Space>
    );
  }

  const pending = tasks.filter((task) => task.status === 'pending');
  const started = tasks.filter((task) => task.status === 'started');
  const emptyCopy = pendingEmptyCopy(activeDepartment);
  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Flex justify="flex-end"><Link to="/completed"><Button>Completed tasks</Button></Link></Flex>
      <Tabs activeKey={activeDepartmentFilter} onChange={changeWorkspace} items={filterTabs} />
      <QueueColumn title="Pending" count={pending.length} empty={<EmptyState title={emptyCopy.title} description={emptyCopy.description} />}>
        {pending.map((task) => card(task))}
      </QueueColumn>
      <QueueColumn title="Started" count={started.length} empty={<EmptyState compact title="Nothing in progress" description="Press Start on a pending job to pick it up." />}>
        {started.map((task) => card(task, task.pickedUpBy === user.id))}
      </QueueColumn>
      {modal}
    </Space>
  );
}

function QueueColumn({ title, count, empty, children }: { title: string; count: number; empty: ReactNode; children: ReactNode }) {
  return (
    <Card title={<span>{title} <span className="ua-count">{count}</span></span>}>
      {count === 0 ? empty : <div className="zen-task-grid">{children}</div>}
    </Card>
  );
}
